import { getArtistMaterialTheme } from "./getArtistMaterialTheme";

export type ExtractedTheme = {
  accent: string;
  tint: string;
  onAccent: string;
  ink?: string;
};

type CacheEntry = ExtractedTheme & { timestamp: number };

// In-memory set to track warned URLs (avoid spam)
const warnedUrls = new Set<string>();

/**
 * Converts RGB values to hex string
 */
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("")}`;
}

/**
 * Extracts dominant color from ImageData using bucketing approach
 */
function extractDominantColorFromImageData(
  imageData: ImageData,
  stride = 2,
): { r: number; g: number; b: number } | null {
  const { data, width, height } = imageData;
  const buckets = new Map<
    number,
    { count: number; sumR: number; sumG: number; sumB: number }
  >();

  // Track all valid pixels for fallback
  let totalR = 0;
  let totalG = 0;
  let totalB = 0;
  let validPixelCount = 0;

  // Iterate pixels with stride
  for (let y = 0; y < height; y += stride) {
    for (let x = 0; x < width; x += stride) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Filter pixels: alpha, near-white, near-black
      if (a < 200) continue; // Skip transparent/low-alpha pixels
      if (r > 240 && g > 240 && b > 240) continue; // Skip near-white
      if (r < 10 && g < 10 && b < 10) continue; // Skip near-black

      // Bucket the pixel
      const br = r >> 4; // 0..15
      const bg = g >> 4;
      const bb = b >> 4;
      const key = (br << 8) | (bg << 4) | bb;

      // Update bucket
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.count++;
        bucket.sumR += r;
        bucket.sumG += g;
        bucket.sumB += b;
      } else {
        buckets.set(key, {
          count: 1,
          sumR: r,
          sumG: g,
          sumB: b,
        });
      }

      // Track for fallback
      totalR += r;
      totalG += g;
      totalB += b;
      validPixelCount++;
    }
  }

  // If no valid pixels, return null
  if (validPixelCount === 0) {
    return null;
  }

  // Find best bucket
  let bestBucket: { r: number; g: number; b: number } | null = null;
  let bestScore = -1;

  for (const bucket of Array.from(buckets.values())) {
    const avgR = bucket.sumR / bucket.count;
    const avgG = bucket.sumG / bucket.count;
    const avgB = bucket.sumB / bucket.count;

    // Compute saturation proxy
    const max = Math.max(avgR, avgG, avgB);
    const min = Math.min(avgR, avgG, avgB);
    const saturation = max === 0 ? 0 : (max - min) / max;

    // Score: count + saturation bias
    const score = bucket.count + saturation * 0.5;

    if (score > bestScore) {
      bestScore = score;
      bestBucket = {
        r: avgR,
        g: avgG,
        b: avgB,
      };
    }
  }

  // Fallback to average if no bucket found
  if (!bestBucket) {
    return {
      r: totalR / validPixelCount,
      g: totalG / validPixelCount,
      b: totalB / validPixelCount,
    };
  }

  return bestBucket;
}

/**
 * Loads an image using fetch with cache bypass for canvas color extraction
 * Uses createImageBitmap if available, falls back to Image with blob URL
 */
async function loadImageForCanvas(
  url: string,
  isCrossOrigin: boolean,
): Promise<{
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  close?: () => void;
}> {
  // Fetch with cache bypass (like DevTools "Disable cache")
  const res = await fetch(url, {
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();

  // Prefer createImageBitmap if available (better performance, no CORS issues with blob)
  if (typeof createImageBitmap !== "undefined") {
    const bmp = await createImageBitmap(blob);
    return {
      width: bmp.width,
      height: bmp.height,
      draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        ctx.drawImage(bmp, 0, 0, w, h);
      },
      close: () => {
        if (bmp.close) {
          bmp.close();
        }
      },
    };
  }

  // Fallback: use Image with blob URL
  const objectUrl = URL.createObjectURL(blob);
  const img = new Image();

  // Set crossOrigin for cross-origin images (safe with blob URL)
  if (isCrossOrigin) {
    img.crossOrigin = "anonymous";
  }

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const result = {
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => {
          ctx.drawImage(img, 0, 0, w, h);
        },
        close: () => {
          URL.revokeObjectURL(objectUrl);
        },
      };
      resolve(result);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image from blob"));
    };
    img.src = objectUrl;
  });
}

/**
 * Extracts artist theme from an image URL with caching and optimization
 */
export async function extractArtistThemeFromImageUrl(
  imageUrl: string,
  opts?: {
    maxSampleSize?: number; // default 64
    cacheKeyPrefix?: string; // default "artistTheme:"
    forceRegen?: boolean; // if true, skip localStorage cache and recompute
  },
): Promise<ExtractedTheme | null> {
  const {
    maxSampleSize = 64,
    cacheKeyPrefix = "artistTheme:",
    forceRegen = false,
  } = opts || {};

  // 1) Sanitize options (force integers)
  const safeMax = Math.max(1, Math.floor(Number(maxSampleSize) || 64));

  // A) Check cache first (unless forceRegen is true)
  const cacheKey = `${cacheKeyPrefix}${imageUrl}`;
  if (!forceRegen) {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed: CacheEntry = JSON.parse(cached);
        // Cache is valid (no expiration check for now, but could add TTL)
        return {
          accent: parsed.accent,
          tint: parsed.tint,
          onAccent: parsed.onAccent,
          ink: parsed.ink,
        };
      }
    } catch {
      // Cache read failed, continue with extraction
    }
  }

  // B) Determine if image is cross-origin
  let isCrossOrigin = false;
  if (typeof window !== "undefined") {
    try {
      const urlObj = new URL(imageUrl, window.location.href);
      isCrossOrigin = urlObj.origin !== window.location.origin;
    } catch {
      // If URL parsing fails, assume same-origin
      isCrossOrigin = false;
    }
  }

  // C) Load image using cache-bypassing fetch
  let imageLoader: {
    width: number;
    height: number;
    draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
    close?: () => void;
  } | null = null;
  try {
    imageLoader = await loadImageForCanvas(imageUrl, isCrossOrigin);
  } catch (error) {
    // Error handling - return null gracefully for CORS/loading errors
    if (!warnedUrls.has(imageUrl)) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isCorsError =
        errorMessage.includes("tainted") ||
        errorMessage.includes("CORS") ||
        errorMessage.includes("cross-origin") ||
        errorMessage.includes("Failed to fetch");

      // Silently handle CORS errors - they're expected for some images
      if (!isCorsError) {
        console.warn(
          `[extractArtistTheme] Failed to load image for color extraction: ${imageUrl}`,
        );
      }
      warnedUrls.add(imageUrl);
    }
    return null;
  }

  if (!imageLoader) {
    return null;
  }

  // 2) After load, compute safe source dims and guard hard
  const sw = imageLoader.width;
  const sh = imageLoader.height;

  if (!Number.isFinite(sw) || !Number.isFinite(sh) || sw <= 0 || sh <= 0) {
    // Cleanup
    imageLoader.close?.();
    return null;
  }

  // D) Downsample before color extraction
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (!ctx) {
    imageLoader.close?.();
    return null;
  }

  // 3) Compute safe integer canvas dims (no NaN/Infinity/0)
  const ratio = Math.min(safeMax / sw, safeMax / sh, 1);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    imageLoader.close?.();
    return null;
  }

  const cw = Math.max(1, Math.floor(sw * ratio) | 0);
  const ch = Math.max(1, Math.floor(sh * ratio) | 0);

  if (!Number.isFinite(cw) || !Number.isFinite(ch) || cw <= 0 || ch <= 0) {
    imageLoader.close?.();
    return null;
  }

  canvas.width = cw;
  canvas.height = ch;

  try {
    // 4) Draw using the loader's draw function
    imageLoader.draw(ctx, cw, ch);

    // 5) Extract dominant color directly from ImageData
    let imageData: ImageData;
    try {
      imageData = ctx.getImageData(0, 0, cw, ch);
    } catch {
      // CORS/tainted canvas error
      if (!warnedUrls.has(imageUrl)) {
        console.warn(
          `[extractArtistTheme] Cannot extract color (CORS/tainted canvas): ${imageUrl}`,
        );
        warnedUrls.add(imageUrl);
      }
      return null;
    }

    // Extract dominant color using bucketing
    const dominantColor = extractDominantColorFromImageData(imageData, 2);

    // Cleanup image loader before returning
    imageLoader.close?.();

    if (!dominantColor) {
      // No valid pixels found
      return null;
    }

    // Convert RGB to hex
    const hex = rgbToHex(dominantColor.r, dominantColor.g, dominantColor.b);

    // Derive theme tokens using existing theme function
    const theme = getArtistMaterialTheme(hex);

    const extractedTheme: ExtractedTheme = {
      accent: theme.primary,
      tint: theme.tint,
      onAccent: theme.onPrimary,
      ink: theme.primaryInk,
    };

    // Store in cache (even if forceRegen, we still cache the new result)
    try {
      const cacheEntry: CacheEntry = {
        ...extractedTheme,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      // Cache write failed (e.g., quota exceeded, private browsing mode)
      // Log for debugging but don't throw - we still return the theme
      if (error instanceof DOMException) {
        if (error.name === "QuotaExceededError") {
          console.warn(
            `[extractArtistTheme] localStorage quota exceeded, cache not saved for: ${imageUrl}`,
          );
        } else {
          console.warn(
            `[extractArtistTheme] localStorage error (${error.name}), cache not saved: ${imageUrl}`,
          );
        }
      }
    }

    return extractedTheme;
  } catch (error) {
    // Cleanup on error
    imageLoader?.close?.();

    // Extraction failed
    if (!warnedUrls.has(imageUrl)) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.warn(
        `[extractArtistTheme] Color extraction failed: ${errorMessage}`,
      );
      warnedUrls.add(imageUrl);
    }
    return null;
  }
}
